import React, { useEffect, useRef } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router";
import { useAppState } from "../providers/AppStateProvider";
import { RootStateNavigator } from "../../domain/appState/models/RootStateNavigator";
import { AuthScreen } from "../screens/Auth/AuthScreen";
import { TermsOfUse } from "../screens/Legal/TermsOfUse";
import { PrivacyPolicy } from "../screens/Legal/PrivacyPolicy";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { AnalyticsFeature } from "../../domain/analytics/analyticsFeature";
import { NotificationsFeature } from "../../domain/notifications/notificationsFeature";
import { lazyWithRetry } from "../utils/lazyWithRetry";
import * as Sentry from "@sentry/react";

// Lazy load routes that aren't immediately needed
const OnboardingRouter = lazyWithRetry(() =>
  import("../screens/Onboarding/OnboardingRouter").then((m) => ({
    default: m.OnboardingRouter,
  })),
);
const AppShell = lazyWithRetry(() =>
  import("../layouts/AppShell").then((m) => ({ default: m.AppShell })),
);

const LoadingScreen: React.FC = () => (
  <div className="flex min-h-dvh items-center justify-center bg-xcf-gradient">
    <LoadingSpinner size={48} />
  </div>
);

const RouterErrorFallback: React.FC = () => (
  <div className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-xcf-gradient px-8 text-center">
    <p className="font-oswald text-[18px] font-normal text-white">
      Something went wrong. Please reload the page.
    </p>
    <button
      type="button"
      onClick={() => window.location.reload()}
      className="bg-white/10 px-6 py-3 font-oswald text-[15px] font-medium text-white transition-opacity hover:opacity-80"
    >
      Reload
    </button>
  </div>
);

/**
 * Resolves the correct path for the current rootState.
 */
const getPathForState = (rootState: RootStateNavigator): string => {
  switch (rootState) {
    case RootStateNavigator.auth:
      return "/auth";
    case RootStateNavigator.onboarding:
      return "/onboarding";
    case RootStateNavigator.partners:
      return "/app";
    default:
      return "/";
  }
};

/**
 * Guard: only renders children if rootState matches allowed states.
 * Otherwise redirects to the correct route for the current state.
 */
const RouteGuard: React.FC<{
  allowedStates: RootStateNavigator[];
  children: React.ReactNode;
}> = ({ allowedStates, children }) => {
  const { rootState } = useAppState();

  if (rootState === RootStateNavigator.splash) {
    return <LoadingScreen />;
  }

  if (!allowedStates.includes(rootState)) {
    return <Navigate to={getPathForState(rootState)} replace />;
  }

  return <>{children}</>;
};

const RootRedirect: React.FC = () => {
  const { rootState } = useAppState();

  if (rootState === RootStateNavigator.splash) {
    return <LoadingScreen />;
  }

  return <Navigate to={getPathForState(rootState)} replace />;
};

const getScreenName = (pathname: string): string => {
  if (pathname.startsWith("/app/deck/") && pathname.endsWith("/matches")) {
    return "/app/deck/:partnershipId/matches";
  }
  if (pathname.startsWith("/app/deck/")) {
    return "/app/deck/:partnershipId";
  }
  if (pathname.startsWith("/app/discover/")) {
    return "/app/discover/:characterId";
  }
  return pathname;
};

const AnalyticsRouteTracker: React.FC = () => {
  const location = useLocation();
  const previousScreenRef = useRef<string | null>(null);

  useEffect(() => {
    const screenName = getScreenName(location.pathname);
    if (previousScreenRef.current === screenName) {
      return;
    }

    previousScreenRef.current = screenName;
    AnalyticsFeature.trackScreenView(screenName);
  }, [location.pathname]);

  return null;
};

export const AppRouter: React.FC = () => {
  useEffect(() => {
    // A push/email deep link lands as `?notif=…&partnershipId=…` — capture it
    // into a pending press (consumed by the Partners surfaces once their data
    // is loaded) and strip the params so a reload doesn't replay it.
    NotificationsFeature.captureRemotePressFromUrl();
  }, []);

  return (
    <BrowserRouter>
      <AnalyticsRouteTracker />
      <Sentry.ErrorBoundary fallback={<RouterErrorFallback />}>
        <React.Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route
              path="/auth"
              element={
                <RouteGuard allowedStates={[RootStateNavigator.auth]}>
                  <AuthScreen />
                </RouteGuard>
              }
            />
            <Route
              path="/onboarding/*"
              element={
                <RouteGuard allowedStates={[RootStateNavigator.onboarding]}>
                  <OnboardingRouter />
                </RouteGuard>
              }
            />
            <Route path="/app/legal/terms-of-use" element={<TermsOfUse />} />
            <Route
              path="/app/legal/privacy-policy"
              element={<PrivacyPolicy />}
            />
            <Route
              path="/app/*"
              element={
                <RouteGuard allowedStates={[RootStateNavigator.partners]}>
                  <AppShell />
                </RouteGuard>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </React.Suspense>
      </Sentry.ErrorBoundary>
    </BrowserRouter>
  );
};
